import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Trash2,
  Mail,
  Clock,
  Crown,
  User,
  RefreshCw,
  AlertCircle
} from 'lucide-react';
import { useOrganization } from '@/hooks/useOrganization';
import { useAuth } from '@/hooks/useAuthApi';
import { 
  getOrganizationInvitations, 
  revokeOrganizationInvitation,
  createOrganizationInvitation
} from '@/services/organizationService';
import { OrganizationInvitation } from '@/types/organization';
import { InviteMemberDialog } from './InviteMemberDialog';
import { toast } from '@/hooks/use-toast';

const INVITATION_STATUS_PRIORITY: Record<string, number> = {
  pending: 4,
  accepted: 3,
  declined: 2,
  expired: 1,
};

const getInvitationTime = (invitation: OrganizationInvitation): number => {
  const createdAt = invitation.createdAt?.getTime?.() ?? 0;
  const expiresAt = invitation.expiresAt?.getTime?.() ?? 0;
  return Math.max(
    Number.isFinite(createdAt) ? createdAt : 0,
    Number.isFinite(expiresAt) ? expiresAt : 0
  );
};

const shouldReplaceInvitation = (
  current: OrganizationInvitation,
  next: OrganizationInvitation
): boolean => {
  const currentPriority = INVITATION_STATUS_PRIORITY[current.status] ?? 0;
  const nextPriority = INVITATION_STATUS_PRIORITY[next.status] ?? 0;

  if (currentPriority !== nextPriority) {
    return nextPriority > currentPriority;
  }

  return getInvitationTime(next) > getInvitationTime(current);
};

const normalizeInvitationEmail = (email: string) => email.trim().toLowerCase();

const getDisplayInvitations = (
  invitations: OrganizationInvitation[]
): OrganizationInvitation[] => {
  const invitationsByEmail = new Map<string, OrganizationInvitation>();

  invitations.forEach((invitation) => {
    const emailKey = normalizeInvitationEmail(invitation.email);
    const currentInvitation = invitationsByEmail.get(emailKey);

    if (!currentInvitation || shouldReplaceInvitation(currentInvitation, invitation)) {
      invitationsByEmail.set(emailKey, invitation);
    }
  });

  return Array.from(invitationsByEmail.values());
};

export function InvitationManager() {
  const [invitations, setInvitations] = useState<OrganizationInvitation[]>([]);
  const [loading, setLoading] = useState(false);
  const [resendingEmail, setResendingEmail] = useState<string | null>(null);
  const { currentOrganization, isAdmin } = useOrganization();
  const { user } = useAuth();

  const loadInvitations = async () => {
    if (!currentOrganization) return;
    
    try {
      setLoading(true);
      const organizationInvitations = await getOrganizationInvitations(currentOrganization.id);
      setInvitations(getDisplayInvitations(organizationInvitations));
    } catch (error) {
      console.error('Error loading invitations:', error);
      toast({
        title: "Error",
        description: "Failed to load invitations.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInvitations();
  }, [currentOrganization]);

  const handleRevokeInvitation = async (invitation: OrganizationInvitation) => {
    if (!currentOrganization) return;
    
    try {
      await revokeOrganizationInvitation(currentOrganization.id, invitation.email);
      toast({
        title: "Invitation Revoked",
        description: "The invitation has been revoked.",
      });
      await loadInvitations();
    } catch (error) {
      console.error('Error revoking invitation:', error);
      toast({
        title: "Error",
        description: "Failed to revoke invitation.",
        variant: "destructive"
      });
    }
  };

  const handleResendInvitation = async (invitation: OrganizationInvitation) => {
    if (!currentOrganization || !user) return;
    
    try {
      setResendingEmail(invitation.email);
      const newInvitation = await createOrganizationInvitation(
        currentOrganization.id,
        invitation.email,
        invitation.role,
        user.uid
      );
      setInvitations((currentInvitations) =>
        getDisplayInvitations([newInvitation, ...currentInvitations])
      );
      toast({
        title: "Invitation Resent",
        description: `A new invitation has been sent to ${invitation.email}.`,
      });
      await loadInvitations();
    } catch (error) {
      console.error('Error resending invitation:', error);
      toast({
        title: "Error",
        description: "Failed to resend invitation.",
        variant: "destructive"
      });
    } finally {
      setResendingEmail(null);
    }
  };

  const handleInvitationCreated = (invitation: OrganizationInvitation) => {
    setInvitations((currentInvitations) =>
      getDisplayInvitations([invitation, ...currentInvitations])
    );
    loadInvitations();
  };

  const formatExpiryDate = (date: Date | undefined): string => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return '';
    return date.toLocaleDateString();
  };

  if (!currentOrganization) {
    return null;
  }

  const isExpired = (invitation: OrganizationInvitation) => invitation.status === 'expired';

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Invitations ({invitations.length})
            </CardTitle>
            <CardDescription>
              Manage invitations to join your organization.
            </CardDescription>
          </div>
          {isAdmin && (
            <InviteMemberDialog onInvitationCreated={handleInvitationCreated} />
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 p-3 border rounded-lg">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="flex-1">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-3 w-32 mt-1" />
                </div>
                <Skeleton className="h-8 w-16" />
                <Skeleton className="h-8 w-8" />
              </div>
            ))}
          </div>
        ) : invitations.length > 0 ? (
          <div className="space-y-3">
            {invitations.map((invitation) => (
              <div key={invitation.id} className={`flex items-center gap-3 p-3 border rounded-lg ${isExpired(invitation) ? 'opacity-75 border-dashed' : ''}`}>
                <div className="flex-shrink-0">
                  <div className={`h-10 w-10 rounded-full flex items-center justify-center ${isExpired(invitation) ? 'bg-destructive/10' : 'bg-muted'}`}>
                    {isExpired(invitation) ? (
                      <AlertCircle className="h-4 w-4 text-destructive" />
                    ) : (
                      <Mail className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium truncate">{invitation.email}</p>
                    <Badge variant={invitation.role === 'admin' ? 'default' : 'secondary'}>
                      {invitation.role === 'admin' ? (
                        <>
                          <Crown className="h-3 w-3 mr-1" />
                          Admin
                        </>
                      ) : (
                        <>
                          <User className="h-3 w-3 mr-1" />
                          Member
                        </>
                      )}
                    </Badge>
                    {isExpired(invitation) && (
                      <Badge variant="destructive">
                        Expired
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    <span>
                      {isExpired(invitation) ? (
                        <>Expired{formatExpiryDate(invitation.expiresAt) ? ` on ${formatExpiryDate(invitation.expiresAt)}` : ''}</>
                      ) : (
                        <>
                          Invited {invitation.createdAt instanceof Date && !isNaN(invitation.createdAt.getTime()) ? invitation.createdAt.toLocaleDateString() : 'recently'}
                          {formatExpiryDate(invitation.expiresAt) && ` · Expires ${formatExpiryDate(invitation.expiresAt)}`}
                        </>
                      )}
                    </span>
                  </div>
                </div>
                
                {isAdmin && (
                  <div className="flex items-center gap-1">
                    {isExpired(invitation) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleResendInvitation(invitation)}
                        disabled={resendingEmail === invitation.email}
                      >
                        <RefreshCw className={`h-4 w-4 mr-1 ${resendingEmail === invitation.email ? 'animate-spin' : ''}`} />
                        {resendingEmail === invitation.email ? 'Sending...' : 'Resend'}
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRevokeInvitation(invitation)}
                      aria-label={isExpired(invitation) ? 'Delete invitation' : 'Revoke invitation'}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-lg border border-dashed p-6 text-center text-muted-foreground">
            <Mail className="h-8 w-8 mx-auto mb-3 opacity-50" />
            <p className="font-medium text-foreground">No invitations yet</p>
            {isAdmin && (
              <p className="text-sm mt-1 text-muted-foreground">
                Invite teammates when you're ready to expand access.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
